import { File, Play, SpeakerLow, WarningCircle } from "@phosphor-icons/react"
import { CircleProgress } from "@/components/circle-progress"
import { cn } from "@/lib/utils"
import { AppointmentAttachmentFragment$data } from "@/modules/waiting-room/services/__generated__/AppointmentAttachmentFragment.graphql"
import { FileUploadStatus } from "@/types/__generated__/union-types"

export type AppointmentAttachment = Omit<AppointmentAttachmentFragment$data[0], " $fragmentType">

const WARNING_STATUS: FileUploadStatus[] = ["MALICIOUS", "ERROR", "SCAN_TIMEOUT"]

const AttachmentImage = ({ media: { thumbnailUrl, url, fileName } }: { media: AppointmentAttachment }) => {
  const imgSrc = thumbnailUrl ?? url
  return (
    <div className="h-full w-full object-cover">
      {imgSrc && <img src={imgSrc} className="aspect-square" alt={fileName} />}
    </div>
  )
}

const AttachmentContent = ({ media, iconClasses }: { media: AppointmentAttachment; iconClasses?: string }) => {
  if (!media.uploadStatus) return null

  if (media.uploadStatus === "UPLOADED") return <CircleProgress className="animate-spin duration-1000" />

  if (WARNING_STATUS.includes(media.uploadStatus)) return <WarningCircle className="text-critical" />

  if (media.attachmentType === "AUDIO") return <SpeakerLow className={iconClasses} />

  if (media.attachmentType === "VIDEO") return <Play className={iconClasses} />

  if (media.attachmentType === "PHOTO") return <AttachmentImage media={media} />

  return <File className={iconClasses} />
}

interface DocumentItemProps extends React.ComponentPropsWithoutRef<"div"> {
  mediaFile: AppointmentAttachment
  remains?: number
}

const AttachmentItem = ({ mediaFile, className, children, remains = 0, ...itemProps }: DocumentItemProps) => {
  const iconClasses = remains > 0 ? "" : "transition-all duration-200 group-hover:scale-125"

  return (
    <div
      data-testid="attachment-item"
      {...itemProps}
      className={cn(
        "group bg-surface-subdued text-on-surface-subdued relative flex h-10 w-10 cursor-pointer items-center justify-center overflow-hidden rounded-sm",
        className,
        mediaFile.uploadStatus &&
          WARNING_STATUS.includes(mediaFile.uploadStatus) &&
          "bg-critical-surface-subdued border-critical-border border",
      )}>
      <AttachmentContent media={mediaFile} iconClasses={iconClasses} />
      {remains > 0 && (
        <div
          data-testid="attachment-remain-count"
          className="bg-on-backdrop text-on-primary absolute inset-0 flex items-center justify-center">
          +{remains}
        </div>
      )}
      {children}
    </div>
  )
}

export { AttachmentItem }
